import type { ReactiveController, ReactiveControllerHost } from 'lit'
import { isServer } from 'lit'

import { transition } from '../events.js'
import { prefersReducedMotion } from '../prefers-reduced-motion.js'

/**
 * The lifecycle of an enter/leave transition.
 * - `closed` / `open` — settled states.
 * - `initial` — the first frame of an enter transition (the "from" styles are
 *   applied; removing them on the next frame commits the transition).
 * - `ending` — a leave transition is running.
 */
export type TransitionStatus = 'closed' | 'initial' | 'open' | 'ending'

export interface TransitionStatusOptions {
  /** Reads the host's logical open state; diffed after every host update. */
  open: () => boolean
  /** The element carrying the CSS transition. Defaults to the host. */
  target?: () => HTMLElement
  /** Called once a completed enter transition settles to `open`. */
  onOpened?: () => void
  /** Called once a completed leave transition settles to `closed`. */
  onClosed?: () => void
}

/**
 * Tracks the enter/leave transition status of a host, mirroring Base UI's
 * `useTransitionStatus`. It does not animate or touch the DOM itself — it
 * exposes a `status` and a `state` object (intended to be reflected to
 * `data-open` / `data-closed` / `data-starting-style` / `data-ending-style` via
 * a {@link StateAttributesController}). Reduced motion / SSR skip the
 * intermediate frames and settle synchronously.
 */
export class TransitionStatusController implements ReactiveController {
  private _status: TransitionStatus = 'closed'
  private previousOpen?: boolean
  private rafId = 0
  /** Bumped on every enter/leave so a stale pending settle is ignored. */
  private token = 0

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: TransitionStatusOptions,
  ) {
    host.addController(this)
  }

  /** The current transition status. */
  get status(): TransitionStatus {
    return this._status
  }

  /** Whether an enter or leave transition is currently running. */
  get transitioning(): boolean {
    return this._status === 'initial' || this._status === 'ending'
  }

  /** The state object to reflect as `data-*` style hooks. */
  get state(): Record<string, boolean> {
    return {
      'open': this._status === 'open' || this._status === 'initial',
      'closed': this._status === 'closed',
      'starting-style': this._status === 'initial',
      'ending-style': this._status === 'ending',
    }
  }

  hostUpdated() {
    const open = this.options.open()
    if (open === this.previousOpen) {
      return
    }
    const first = this.previousOpen === undefined
    this.previousOpen = open

    // The first observed value is a settled state — no transition, no events.
    if (first) {
      this._status = open ? 'open' : 'closed'
      return
    }

    if (open) {
      this.enter()
    }
    else {
      this.leave()
    }
  }

  hostDisconnected() {
    this.cancelRaf()
    // Invalidate any pending settle so a late transitionend can't change state
    // after disconnect.
    this.token++
  }

  private enter() {
    this.cancelRaf()
    this.token++

    if (prefersReducedMotion() || isServer) {
      this._status = 'open'
      this.options.onOpened?.()
      this.host.requestUpdate()
      return
    }

    // Apply the "from" frame (data-starting-style), then commit on the next
    // frame so a CSS enter transition has a defined starting point.
    this._status = 'initial'
    this.host.requestUpdate()
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0
      this._status = 'open'
      this.options.onOpened?.()
      this.host.requestUpdate()
    })
  }

  private leave() {
    this.cancelRaf()
    const token = ++this.token

    if (prefersReducedMotion() || isServer) {
      this._status = 'closed'
      this.options.onClosed?.()
      this.host.requestUpdate()
      return
    }

    this._status = 'ending'
    this.host.requestUpdate()

    const target = this.options.target?.() ?? this.host
    void transition(target).then(() => {
      // Ignore if re-opened (or disconnected) before the leave completed.
      if (token !== this.token) {
        return
      }
      this._status = 'closed'
      this.options.onClosed?.()
      this.host.requestUpdate()
    })
  }

  private cancelRaf() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  }
}
